import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { DeviceMobile, ArrowLeft } from "@phosphor-icons/react";

export default function OtpLoginPage() {
  const { otpSend, otpVerify, formatApiError } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await otpSend(phone);
      toast.success(res.message || "OTP sent");
      if (res.dev_otp) setDevOtp(res.dev_otp);
      setStep("otp");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await otpVerify(phone, otp, name);
      toast.success("Signed in!");
      nav("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <div className="w-14 h-14 rounded-2xl bg-gold text-midnight flex items-center justify-center mb-6">
        <DeviceMobile size={28} weight="duotone" />
      </div>
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Quick sign in</p>
      <h1 className="font-serif text-4xl text-midnight mb-2">
        {step === "phone" ? "Sign in with OTP" : "Enter OTP"}
      </h1>
      <p className="text-sm text-neutral-500 mb-8">
        {step === "phone"
          ? "We'll send a 6-digit code to your mobile"
          : `Sent to +91 ${phone}. Enter the 6-digit code (valid 5 min).`}
      </p>
      {step === "otp" && devOtp && (
        <div className="mb-6 px-4 py-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900" data-testid="dev-otp-banner">
          <p className="text-[11px] tracking-widest uppercase mb-1">Dev mode</p>
          <p className="text-sm">
            SMS not configured. Your OTP is: <code className="bg-white px-2 py-0.5 rounded text-base font-semibold tracking-widest">{devOtp}</code>
          </p>
        </div>
      )}

      {step === "phone" ? (
        <form onSubmit={sendOtp} className="space-y-5" data-testid="phone-form">
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Mobile Number</label>
            <div className="flex gap-2">
              <div className="px-4 py-2 rounded-lg border border-input bg-stone flex items-center text-sm">+91</div>
              <Input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit number"
                required
                data-testid="otp-phone-input"
              />
            </div>
          </div>
          <Button type="submit" disabled={loading || phone.length !== 10} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="send-otp-btn">
            {loading ? "Sending..." : "Send OTP"}
          </Button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-5" data-testid="otp-form">
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Your name (new users)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul Sharma" data-testid="otp-name-input" />
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">6-digit OTP</label>
            <Input
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              required
              data-testid="otp-code-input"
            />
          </div>
          <Button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="verify-otp-btn">
            {loading ? "Verifying..." : "Verify & Continue"}
          </Button>
          <button type="button" onClick={() => setStep("phone")} className="text-sm text-neutral-500 hover:text-midnight flex items-center gap-1">
            <ArrowLeft size={14} /> Change phone number
          </button>
        </form>
      )}
    </div>
  );
}
